import { useMemo, useState, useRef } from 'react'
import { LoadingIcon, Button, FullScreenLoadingCover } from '@prosolve/common-components-ui'
import Papa from "papaparse"
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'; 
import DetailsModal from './DetailsModal';
import moment from 'moment';
import { FaFlag, FaCheck, FaTimes, FaCheckCircle, FaBookOpen, FaDownload } from "react-icons/fa";
import useFetch from '../../../../hooks/useFetch';
import ApprovalsModal from './ApprovalsModal';
import CompletionApprovalsModal from './CompletionApprovalsModal';
import CustomApprovalFilter from './CustomApprovalFilter';
import { downloadFile } from "../../../../utils"

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

export default ({
  assessmentData = {},
  isLoading,
  analyticsData = [],
  reloadData,
}) => {
  const { _id: assessmentId, name: assessmentName, questions: assessmentQuestions = [], reportConfig = {} } = assessmentData;

  const {
    hasApprovals,
    hideIdFields,
    hideQuestFields
  } = reportConfig

  const fetch = useFetch();

  const flagFieldTextDisplay = (data) => {
    const {
      flag,
      approval: {
        status = '',
        note = '',
      } = {}
    } = data;
    const statusDisplayUpper = status ? status === 'completed' ? 'Paid' : (status.charAt(0).toUpperCase() + status.slice(1)) : ''
    const statusDisplay = `${statusDisplayUpper}${status === 'rejected' && Boolean(note) ? ` - Note: ${note}` : ''}`
    if (flag && statusDisplay) {
      return `Needs Attention - ${statusDisplay}`
    } else if (flag) {
      return 'Needs Attention'
    } else if (statusDisplay) {
      return statusDisplay
    } else {
      return ''
    }
  }

  const processedData = useMemo(() => {
    if (analyticsData && Array.isArray(analyticsData) && analyticsData.length > 0) {
      // 1. Static fields
      const staticColsRaw = [
        { 
          field: 'flag', 
          headerName: '',
          width: 50,
          maxWidth: 50,
          minWidth: 50,
          pinned: 'left',
          lockPinned: true,
          suppressMovable: true,
          lockPosition: 'left',
          filter: CustomApprovalFilter,
          filterParams: { hasApprovals },
          tooltipValueGetter: (params) => flagFieldTextDisplay(params.data || {}),
          cellRenderer: (params) => {
            const data = params.data || {}
            const {
              flag,
              approval: {
                status = ''
              } = {}
            } = data;

            const iconStyle = flag ? { position: 'absolute', top: '22px', left: '16px' } : {}

            let statusIcon = status === 'approved' ?
              <FaCheck style={{ ...iconStyle, color: '#4caf50' }} /> :
              status === 'rejected' ?
              <FaTimes style={{ ...iconStyle, color: '#f85c5c' }} /> :
              status === 'completed' ?
              <FaCheckCircle style={{ ...iconStyle, color: '#0280c6' }} />
              : undefined

            if (flag && statusIcon) {
              return (
                <>
                  <FaFlag style={{ position: 'absolute', top: '4px', left: '16px', color: '#f85c5c' }} />
                  { statusIcon }
                </>
              )
            } else if (flag) {
              return <FaFlag style={{ color: '#f85c5c' }} />
            } else if (statusIcon) {
              return statusIcon;
            } else {
              return ''
            }
          } 
        },
        { field: 'userEmail', headerName: 'User Email' },
        ...(
          !hideQuestFields ? [
            { field: 'className', headerName: 'Class Name' }
          ] : []
        ),
      ];

      const staticColsAfterQsRaw = [
        { field: 'accountName', headerName: 'Account Name' },
        ...(
          !hideIdFields ? [
            { field: 'accountId', headerName: 'Account ID' },
            { field: 'implementationId', headerName: 'Implementation ID' },
         ] : []
        ),
        ...(
          !hideQuestFields ? [
            { field: 'classId', headerName: 'Class ID' },
            { field: 'seasonName', headerName: 'Season Name' },
            { field: 'episodeId', headerName: 'Episode ID' },
         ] : []
        ),
        { field: 'eventName', headerName: 'Event Name' },
        ...(
          !hideIdFields ? [
            { field: 'eventId', headerName: 'Event ID' },
            { field: 'userId', headerName: 'User ID' },
          ] : []
        ),
        ...(
          !hideQuestFields ? [
            { field: 'territory', headerName: 'Territory' },
            { field: 'implementationStartDate', headerName: 'Implementation Start Date' },
            { field: 'implementationEndDate', headerName: 'Implementation End Date' },
          ] : []
        ),
        ...(
          hasApprovals ? [
            { 
              field: 'approval.paidDate', 
              headerName: 'Paid Date',
              valueFormatter: (params) => {
                if (params.value) {
                  const dateTime = moment(params.value);
                  return dateTime.isValid() ? dateTime.format('MM/DD/YY') : '';
                } else {
                  return '';
                }
              } 
            }
          ] : []
        ),
        { 
          field: 'dateTime', 
          headerName: 'Submission Date', 
          valueFormatter: (params) => {
            const dateTime = moment(params.value);
            return dateTime.isValid() ? dateTime.format('MM/DD/YY h:mm:ss A') : '';
          }
        }
      ];

      // Add on the tooltipFields and headerTooltip since they are just dups of the the field and header name
      const staticColMapFn = col => {
        if (col.field === 'flag') {
          return col
        } else {
          return { ...col, tooltipField: col.field, headerTooltip: col.headerName, lockPinned: true }
        }
      }

      const staticCols = staticColsRaw.map(staticColMapFn)

      const staticColsAfterQs = staticColsAfterQsRaw.map(staticColMapFn)

      // 2. Get unique questions across all rows
      const questionSet = new Map();
      analyticsData.forEach((item) => {
        const isFromActiveAssessment = item?.assessmentId === assessmentId
        item.answers.forEach(({ questionId, question, questionType }) => {
          const matchedQ = assessmentQuestions.find(({ id }) => Number(id) === Number(questionId)) || {}
          const { sort } = matchedQ;
          if (!questionSet.has(questionId)) {
            questionSet.set(questionId, { question, questionType, sort });
          } else if (isFromActiveAssessment) { // ensure active assessment question override older assessment questions
            questionSet.set(questionId, { question, questionType, sort });
          }
        });
      });

      // 3. Create dynamic columns for questions
      const questionCols = Array.from(questionSet.entries())
        .sort((a, b) => a[1].sort - b[1].sort)
        .map(([id, { question, questionType }]) => {
          const qCol = {
            field: `question_${id}`,
            headerName: question,
            headerTooltip: question,
            // flex: 1,
            tooltipField: `question_${id}`,
            lockPinned: true
          }
          if (questionType === 'fileupload') {
            qCol.cellRenderer = (params) => {
              const value = params.value || ''
              const hasMulti = value.indexOf(' ; ') > -1;
              return (
                <div 
                  style={{
                    color: '#0280c6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setDetailsOpen({ 
                      answers :[{
                        answer: value,
                        questionType: 'fileupload'
                      }],
                      isFilesOnly: true,
                      hasMulti
                    })
                  }}
                >
                  {value ? `View Uploaded File${hasMulti ? 's' : ''}` : ''}
                </div>
              )
            }
          }
          return qCol;
        });

      // 4. Flatten answers into row fields
      const flattenedRows = analyticsData.map((item) => {
        const answerMap = {};
        item.answers.forEach(({ questionId, answer }) => {
          answerMap[`question_${questionId}`] = answer;
        });
        return {
          ...item,
          ...answerMap,
        };
      });

      return {
        columnDefs: [...staticCols, ...questionCols, ...staticColsAfterQs],
        rowData: flattenedRows
      }
    } else {
      return {
        columnDefs: [],
        rowData: []
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsData]);

  const { columnDefs, rowData } = processedData;

  const [selectedData, setSelectedData] = useState()

  const [detailsOpen, setDetailsOpen] = useState()

  const [approvalsOpen, setApprovalsOpen] = useState()

  const [completionApprovalsOpen, setCompletionApprovalsOpen] = useState(false)

  const [approvalUpdatesInProgress, setApprovalUpdatesInProgress] = useState(false)

  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes()
    const selectedData =  selectedNodes.map(selectedNode => selectedNode?.data)
    setSelectedData(selectedData)
  };

  const toggleFlag = async () => {
    const results = await Promise.all(
      selectedData.map(async selectedDataItem => {
        const { _id: assessmentAnswerId, flag } = selectedDataItem;

        const flagUpateVal = flag ? false : true

        const response = await fetch(`assessment/updateAnswerFlag`,
          {
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ assessmentAnswerId, flag: flagUpateVal })
          })

        return await response.json()
      })
    )

    const hasSuccess = results.find(result => result?.success)

    if (hasSuccess) {
      reloadData()
      setSelectedData(undefined)
    }
  }

  const updateAnswerApproval = async (status = '', note = '', paidDate = '') => {
    setApprovalUpdatesInProgress(true)
    try {
      const results = []
      for(let i = 0; i < selectedData.length; i++) {
        const selectedDataItem = selectedData[i]
        const { _id: assessmentAnswerId, eventId, userEmail, answers = [], approval: { status: prevStatus = '' } = {} } = selectedDataItem;

        const postData = {
          assessmentAnswerId, 
          status
        }
        if (note) {
          postData.note = note;
        }
        if (paidDate) {
          postData.paidDate = paidDate;
        }
        if (prevStatus) {
          postData.prevStatus = prevStatus;
        }

        // The below extra logic is very specific scenario only used for TrainerReimbursementForm assessment
        if (assessmentName === 'TrainerReimbursementForm') {
          if (eventId) {
            postData.eventId = eventId;
          }
          if (userEmail) {
            postData.userEmail = userEmail;
          }
          const reimbursementAnswerObj = answers.find(({ questionId }) => questionId === '5') || {}

          const { answer: reimbursementAnswer = '' } = reimbursementAnswerObj;
          const reimbursementTotal = Number(reimbursementAnswer)

          const reimbursementTypeAnswerObj = answers.find(({ questionId }) => questionId === '2') || {}
          const { answer: reimbursementTypeAnswer = '' } = reimbursementTypeAnswerObj;

          const finalReimbursementTotal = reimbursementTypeAnswer === 'Mileage Reimbursement' ? 
            Math.round((reimbursementTotal * 0.76) * 100) / 100 :
            reimbursementTotal

          if (Number.isFinite(finalReimbursementTotal)) {
            postData.reimbursementTotal = finalReimbursementTotal;
          }
        }

        const response = await fetch(`assessment/updateAnswerApproval`, 
          { 
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(postData)
          })

        const responseObj = await response.json()
        results.push(responseObj)
      }

      const hasSuccess = results.find(result => result?.success)

      if (hasSuccess) {
        reloadData()
        setSelectedData(undefined)
      }
    } catch(e) {
      console.log(e)
    }
    setApprovalUpdatesInProgress(false)
  }

  const gridRef = useRef()

  const onGridReady = (params) => {
    if (hasApprovals) {
      params.api.applyColumnState({
        state: [
          { colId: 'dateTime', sort: 'desc' }
        ]
      });

      params.api.setFilterModel({
        flag: { waiting: true }
      });
    }
  };

  const onFirstDataRendered = () => {
    gridRef.current.api.autoSizeAllColumns()
  }

  const onDownloadData = () => {
    const api = gridRef.current.api;
    const columns = api.getAllDisplayedColumns();

    const data = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.group) return; // make sure to skip potential group nodes
      const builtInColFieldKeys = ['ag-Grid-SelectionColumn'] // skip built in or custom cols

      const row = {};
      for (const col of columns) {
        const colDef = col.getColDef();
        const key = colDef.headerName || colDef.field || col.getColId();
        if (!builtInColFieldKeys.includes(key)) {
          const value = key === 'flag' ? flagFieldTextDisplay(node?.data || {}) : api.getCellValue({ rowNode: node, colKey: col.getColId(), useFormatter: true }) ?? ''
          row[key] = value
        }
      }
      data.push(row);
    });

    const currentDateTime = moment().format('MM/DD/YY-HH:mm:ss')
    const filename = `${assessmentName} - ${currentDateTime}.csv`

    downloadFile({ 
      filename: filename,
      fileDataParts: [Papa.unparse(data)], 
      type: 'text/csv;charset=utf-8'
    })
  }

  const buttonStyle = {
    flexDirection: 'column',
    width: '90px',
    marginLeft: '10px',
    textAlign: 'center'
  }

  const approvalButtonStyle = {
    ...buttonStyle,
    border: 'none',
    width: '50px',
    padding: '2px 10px'
  }

  const hasDataSelected = selectedData && selectedData.length > 0;

  const isDataSelectedSingleItem = selectedData && selectedData.length === 1;

  if (isLoading) {
    return (
      <div style={{
        marginTop: '100px',
        flex: 1,
        display: 'flex',
        justifyContent: 'center'
      }}>
        <LoadingIcon />
      </div>
    )
  } 
  else {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>

        {approvalUpdatesInProgress && <FullScreenLoadingCover /> }

        {
          Boolean(detailsOpen) &&
          <DetailsModal
            data={detailsOpen}
            onClose={() => setDetailsOpen(undefined)}
          />
        }

        {
          Boolean(approvalsOpen) &&
          <ApprovalsModal
            data={approvalsOpen}
            onClose={() => setApprovalsOpen(undefined)}
            onConfirmation={(note = '') => {
              if (selectedData) {
                updateAnswerApproval('rejected', note)
              }
              setApprovalsOpen(undefined)
            }}
          />
        }

        {
          completionApprovalsOpen &&
          <CompletionApprovalsModal
            onClose={() => setCompletionApprovalsOpen(false)}
            onConfirmation={(paidDate = '') => {
              if (selectedData) {
                updateAnswerApproval('completed', '', paidDate)
              }
              setCompletionApprovalsOpen(false)
            }}
          />
        }

        <div style={{ display: 'flex', position: 'absolute', top: '-46px', right: '0px' }}>
          {
            hasApprovals &&
            <div style={{
              color: '#565656',
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #c4c4c4',
              borderRadius: '5px',
              background: hasDataSelected ? '#ffffff' : '#e4e4e4',
              padding: '2px 20px'
            }}>
              <div style={{ fontWeight: 'normal', borderRight: '1px solid #c4c4c4', paddingRight: '20px' }}>
                {'Approvals'}
              </div>
              <Button
                style={approvalButtonStyle}
                $disabled={!hasDataSelected}
                onClick={() => {
                  if (hasDataSelected) {
                    updateAnswerApproval('approved')
                  }
                }}
              >
                <FaCheck style={{ marginBottom: '2px', color: '#4caf50', fontSize: '14px' }} />
                <span style={{ color: '#565656', fontSize: '14px' }}>{'Approve'}</span>
              </Button>
              <Button
                style={approvalButtonStyle}
                $disabled={!isDataSelectedSingleItem}
                onClick={() => {
                  if (isDataSelectedSingleItem) {
                    setApprovalsOpen(selectedData[0])
                  }
                }}
              >
                <FaTimes style={{ marginBottom: '2px', color: '#f85c5c', fontSize: '14px' }} />
                <span style={{ color: '#565656', fontSize: '14px' }}>{'Reject'}</span>
              </Button>
              <Button
                style={approvalButtonStyle}
                $disabled={!hasDataSelected}
                onClick={() => {
                  if (hasDataSelected) {
                    setCompletionApprovalsOpen(true)
                    // updateAnswerApproval('completed')
                  }
                }}
              >
                <FaCheckCircle style={{ marginBottom: '2px', color: '#0280c6', fontSize: '14px' }} />
                <span style={{ color: '#565656', fontSize: '14px' }}>{'Paid'}</span>
              </Button>
            </div>
          }
          <Button
            style={buttonStyle}
            $disabled={!hasDataSelected}
            onClick={() => {
              if (hasDataSelected) {
                toggleFlag()
              }
            }}
          >
            <FaFlag style={{ marginBottom: '2px', color: '#0280c6', fontSize: '14px' }} />
            <span style={{ color: '#565656', fontSize: '14px' }}>{'Toggle Flag'}</span>
          </Button>
          <Button
            style={buttonStyle}
            $disabled={!isDataSelectedSingleItem}
            onClick={() => {
              if (isDataSelectedSingleItem) {
                setDetailsOpen(selectedData[0])
              }
            }}
          >
            <FaBookOpen style={{ marginBottom: '2px', color: '#0280c6', fontSize: '14px' }} />
            <span style={{ color: '#565656', fontSize: '14px' }}>{'View Details'}</span>
          </Button>
          <Button
            style={buttonStyle}
            onClick={() => {
              onDownloadData()
            }}
          >
            <FaDownload style={{ marginBottom: '2px', color: '#0280c6', fontSize: '14px' }} />
            <span style={{ color: '#565656', fontSize: '14px' }}>{'Download'}</span>
          </Button>
        </div>

        <div style={{ width: '100%', height: 'calc(100vh - 167px)' }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={{ sortable: true, resizable: true, filter: true, tooltipComponent: 'agTooltipComponent' }}
            tooltipShowDelay={300} 
            tooltipShowMode={"whenTruncated"}
            rowSelection={{
              // mode: 'singleRow',
              mode: 'multiRow',
              selectAll: "filtered",
              // checkboxes: true,
              // headerCheckbox: false,
              // enableClickSelection: true
            }}
            selectionColumnDef={{
              pinned: 'left',
              lockPinned: true,
              lockPosition: 'left',
              suppressMovable: true,
              width: 50,
              maxWidth: 50,
              minWidth: 50
            }}
            onSelectionChanged={onSelectionChanged}
            enableCellTextSelection={true}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
          />
        </div>

      </div>
    )
  }
}